import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import { useSidebar } from '../../contexts/SidebarContext';
import ProductionSheetHeader from './components/ProductionSheetHeader';
import ProductionSheetBody, {
  batchUpdatePayload,
  emptyProductionForm,
  formFromBatch,
  validateProductionForm,
} from './components/ProductionSheetBody';
import ProductionSheetLabels from './components/ProductionSheetLabels';
import { hasPackagingDate } from '../../lib/productionLabelUtils';
import { supabase } from '../../lib/supabase';
import {
  CASE_SHEET_SELECT,
  mapCaseToSheetPatientData,
} from '../../lib/caseSheetPatientData';
import { fetchConceptionPhaseTotalAligners } from '../../services/caseProductionPhasesService';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const ProductionSheetPage = () => {
  const [searchParams] = useSearchParams();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const sheetRef = useRef(null);
  const labelsRef = useRef(null);

  const caseIdParam = searchParams.get('caseId');
  const batchIdParam = searchParams.get('batchId');
  const milestoneIdParam = searchParams.get('milestoneId');
  const readOnlyParam = searchParams.get('readOnly') === '1';

  const [loading, setLoading] = useState(Boolean(caseIdParam || batchIdParam));
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [patientData, setPatientData] = useState(null);
  const [batch, setBatch] = useState(null);
  const [form, setForm] = useState(emptyProductionForm);
  const [errors, setErrors] = useState({});
  const [milestoneCompleted, setMilestoneCompleted] = useState(false);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'Production', path: '/production-tracking-dashboard', icon: 'Factory' },
    { label: 'Fiche de production', path: '/production-sheet', icon: 'FileText' },
  ];

  const setField = useCallback(
    (field) => (e) => {
      const value = e?.target ? e.target.value : e;
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  useEffect(() => {
    if (!caseIdParam && !batchIdParam) {
      setPatientData(null);
      setBatch(null);
      setForm(emptyProductionForm());
      setLoadError(null);
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        let batchRow = null;

        if (batchIdParam) {
          const { data, error } = await supabase
            .from('production_batches')
            .select('*')
            .eq('id', batchIdParam)
            .maybeSingle();
          if (error) throw error;
          batchRow = data;
        } else if (caseIdParam) {
          const { data, error } = await supabase
            .from('production_batches')
            .select('*')
            .eq('case_id', caseIdParam)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          if (error) throw error;
          batchRow = data;
        }

        const resolvedCaseId = caseIdParam || batchRow?.case_id;
        if (!resolvedCaseId) {
          throw new Error('Aucun cas associé à cette fiche de production.');
        }

        const { data: caseInfo, error: caseError } = await supabase
          .from('cases')
          .select(CASE_SHEET_SELECT)
          .eq('id', resolvedCaseId)
          .single();
        if (caseError) throw caseError;

        setPatientData(mapCaseToSheetPatientData(caseInfo));

        const conceptionAligners = await fetchConceptionPhaseTotalAligners(resolvedCaseId);
        const nextForm = formFromBatch(batchRow, conceptionAligners ?? '');
        setBatch(batchRow);
        setForm(nextForm);
        setMilestoneCompleted(false);

        // Persiste UP/LOW « À » issus de la conception si plages encore par défaut
        const shouldPersistRanges =
          batchRow?.id &&
          !batchRow.packaging_date &&
          (Number(batchRow.model_up_to) !== Number(nextForm.model_up_to) ||
            Number(batchRow.model_low_to) !== Number(nextForm.model_low_to));

        if (shouldPersistRanges) {
          const { error: rangeErr } = await supabase
            .from('production_batches')
            .update({
              model_up_from: Number(nextForm.model_up_from),
              model_up_to: Number(nextForm.model_up_to),
              model_low_from: Number(nextForm.model_low_from),
              model_low_to: Number(nextForm.model_low_to),
              updated_at: new Date().toISOString(),
            })
            .eq('id', batchRow.id);
          if (!rangeErr) {
            setBatch((prev) =>
              prev
                ? {
                    ...prev,
                    model_up_from: Number(nextForm.model_up_from),
                    model_up_to: Number(nextForm.model_up_to),
                    model_low_from: Number(nextForm.model_low_from),
                    model_low_to: Number(nextForm.model_low_to),
                  }
                : prev
            );
          }
        }
      } catch (err) {
        console.error('[ProductionSheetPage] load error:', err);
        setPatientData(null);
        setBatch(null);
        setForm(emptyProductionForm());
        setLoadError(err.message || 'Fiche introuvable');
        toast.error('Erreur lors du chargement : ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [caseIdParam, batchIdParam]);

  const handleSave = async () => {
    if (!batch?.id) {
      toast.warning('Aucun lot de production trouvé pour ce cas.');
      return;
    }
    const errs = validateProductionForm(form);
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    setErrors({});
    try {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from('production_batches')
        .update({ ...batchUpdatePayload(form), updated_at: now })
        .eq('id', batch.id);
      if (error) throw error;

      setBatch((prev) => (prev ? { ...prev, ...batchUpdatePayload(form) } : prev));

      if (milestoneIdParam && !milestoneCompleted) {
        const { error: msError } = await supabase
          .from('phase_milestones')
          .update({ is_completed: true, completed_date: now, updated_at: now })
          .eq('id', milestoneIdParam);
        if (msError) {
          console.warn('[ProductionSheetPage] milestone completion failed:', msError.message);
          toast.warning("Fiche enregistrée, mais le jalon n'a pas pu être validé automatiquement.");
        } else {
          setMilestoneCompleted(true);
          toast.success('Fiche de production enregistrée et jalon validé !');
          setSaving(false);
          return;
        }
      }

      toast.success('Fiche de production enregistrée !');
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement : " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const clearPrintMode = useCallback(() => {
    document.body.classList.remove('print-sheet-only', 'print-labels-only');
  }, []);

  useEffect(() => {
    window.addEventListener('afterprint', clearPrintMode);
    return () => window.removeEventListener('afterprint', clearPrintMode);
  }, [clearPrintMode]);

  const handlePrintSheet = () => {
    clearPrintMode();
    document.body.classList.add('print-sheet-only');
    window.print();
  };

  const handlePrintLabels = async () => {
    const from = Number(form?.label_from);
    const to = Number(form?.label_to);
    if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) {
      toast.error('Renseignez la plage Étiquette De / À avant d’imprimer.');
      return;
    }
    if (!hasPackagingDate(form?.packaging_date)) {
      toast.error('Renseignez la date d’emballage avant d’imprimer les étiquettes.');
      return;
    }
    if (batch?.id && batch.packaging_date?.slice?.(0, 10) !== form.packaging_date) {
      const { error } = await supabase
        .from('production_batches')
        .update({
          packaging_date: form.packaging_date,
          updated_at: new Date().toISOString(),
        })
        .eq('id', batch.id);
      if (error) {
        toast.error("Impossible d'enregistrer la date d'emballage : " + error.message);
        return;
      }
      setBatch((prev) =>
        prev ? { ...prev, packaging_date: form.packaging_date } : prev
      );
    }
    clearPrintMode();
    document.body.classList.add('print-labels-only');
    window.print();
  };

  const handleExportPDF = async () => {
    if (!sheetRef.current) return;
    setPdfLoading(true);
    const el = sheetRef.current;
    el.classList.add('ipr-pdf-export');
    try {
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        onclone: (doc) => {
          doc.querySelectorAll('.ipr-pdf-hide').forEach((node) => {
            node.style.display = 'none';
          });
        },
      });

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 8;
      const usableWidth = pageWidth - margin * 2;
      const usableHeight = pageHeight - margin * 2;
      const imgHeight = (canvas.height * usableWidth) / canvas.width;
      const imgData = canvas.toDataURL('image/png');

      let heightLeft = imgHeight;
      let offsetY = margin;
      pdf.addImage(imgData, 'PNG', margin, offsetY, usableWidth, imgHeight);
      heightLeft -= usableHeight;

      while (heightLeft > 1) {
        offsetY = margin - (imgHeight - heightLeft);
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', margin, offsetY, usableWidth, imgHeight);
        heightLeft -= usableHeight;
      }

      pdf.save(`Fiche-Production-${patientData?.caseNumber || 'export'}-${Date.now()}.pdf`);
    } catch (err) {
      console.error('[ProductionSheetPage] PDF export error:', err);
      toast.error('Erreur lors de la génération du PDF');
    } finally {
      el.classList.remove('ipr-pdf-export');
      setPdfLoading(false);
    }
  };

  const canEdit = Boolean(batch) && !readOnlyParam;
  const displayBatch = batch
    ? { ...batch, ...batchUpdatePayload(form) }
    : null;

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />

        <div className="main-content">
          <div className="p-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="flex items-center justify-between mb-6 ipr-sheet-shell px-5 py-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Fiche de Production</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Instructions atelier — traçabilité lot / cas
                  {patientData && (
                    <span className="ml-2 inline-flex items-center gap-1 text-primary font-medium">
                      · {patientData.caseNumber}
                      {batch && (
                        <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-normal">
                          {batch.batch_number || 'Lot'}
                        </span>
                      )}
                    </span>
                  )}
                </p>
              </div>
              <div className="flex gap-3 flex-wrap justify-end">
                {canEdit && (
                  <Button
                    variant="outline"
                    iconName="Save"
                    onClick={handleSave}
                    loading={saving}
                    disabled={saving || loading}
                  >
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                )}
                <Button
                  variant="outline"
                  iconName="Printer"
                  onClick={handlePrintSheet}
                  disabled={!patientData}
                >
                  Imprimer la fiche
                </Button>
                <Button
                  variant="outline"
                  iconName="Tags"
                  onClick={handlePrintLabels}
                  disabled={!patientData}
                >
                  Imprimer les étiquettes
                </Button>
                <Button
                  variant="default"
                  iconName="Download"
                  onClick={handleExportPDF}
                  loading={pdfLoading}
                  disabled={!patientData || pdfLoading}
                >
                  Exporter PDF
                </Button>
              </div>
            </div>

            {!caseIdParam && !batchIdParam && (
              <div className="ipr-sheet-shell border-2 border-dashed border-gray-200 flex flex-col items-center justify-center py-20 text-center">
                <p className="text-base font-semibold text-gray-700 mb-1">Aucune fiche à afficher</p>
                <p className="text-sm text-gray-400 max-w-sm mb-4">
                  Ouvrez la fiche de production depuis un dossier (timeline des phases) ou un lot atelier.
                </p>
                <Button asChild variant="outline" iconName="Briefcase">
                  <Link to="/cases-list">Retour aux dossiers</Link>
                </Button>
              </div>
            )}

            {(caseIdParam || batchIdParam) && loading && (
              <div className="ipr-sheet-shell flex items-center justify-center py-20">
                <div className="flex flex-col items-center gap-3 text-gray-400">
                  <svg className="animate-spin h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  <p className="text-sm">Chargement de la fiche...</p>
                </div>
              </div>
            )}

            {(caseIdParam || batchIdParam) && !loading && loadError && (
              <div className="ipr-sheet-shell border-2 border-dashed border-destructive/30 flex flex-col items-center justify-center py-20 text-center">
                <p className="text-base font-semibold text-gray-700 mb-1">Impossible de charger la fiche</p>
                <p className="text-sm text-gray-400 max-w-sm mb-4">{loadError}</p>
                {caseIdParam && (
                  <Button asChild variant="outline" iconName="ArrowLeft">
                    <Link to={`/case-detail/${caseIdParam}`}>Retour au dossier</Link>
                  </Button>
                )}
              </div>
            )}

            {(caseIdParam || batchIdParam) && !loading && patientData && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="ipr-section-surface p-4">
                    <div className="text-sm font-medium text-gray-500 mb-1">Patient</div>
                    <div className="text-base font-semibold text-gray-900 truncate">{patientData.patientName}</div>
                  </div>
                  <div className="ipr-section-surface p-4">
                    <div className="text-sm font-medium text-gray-500 mb-1">N° Cas</div>
                    <div className="text-base font-semibold text-gray-900">{patientData.caseNumber}</div>
                  </div>
                  <div className="ipr-section-surface p-4">
                    <div className="text-sm font-medium text-gray-500 mb-1">N° Lot</div>
                    <div className="text-base font-semibold text-gray-900">
                      {batch?.batch_number || '—'}
                    </div>
                  </div>
                  <div className="ipr-section-surface p-4">
                    <div className="text-sm font-medium text-gray-500 mb-1">Nombre d&apos;aligneurs</div>
                    <div className="text-base font-semibold text-gray-900">
                      {Number(form.model_up_to || 0) + Number(form.model_low_to || 0)}
                    </div>
                  </div>
                </div>

                {!batch && (
                  <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    Aucun lot de production trouvé pour ce cas. Les champs restent en lecture seule tant
                    qu&apos;un lot n&apos;est pas créé.
                  </div>
                )}

                <div ref={sheetRef} className="production-print-root">
                  <div className="production-sheet-page-1 ipr-sheet-shell print:shadow-none print:border-0 bg-white">
                    <ProductionSheetHeader patientData={patientData} batch={displayBatch} />
                    <ProductionSheetBody
                      form={form}
                      setField={setField}
                      errors={errors}
                      readOnly={!canEdit}
                    />
                    <div className="border-t border-gray-200/80 p-4 md:p-5 ipr-section-surface print:p-2">
                      <h3 className="font-semibold text-gray-900 mb-2 text-sm print:text-xs print:mb-1">
                        Instructions importantes
                      </h3>
                      <ul className="text-xs text-gray-600 space-y-1.5 columns-1 md:columns-2 gap-6 print:space-y-0.5 print:columns-2">
                        {[
                          'Vérifier les plages UP / LOW avant découpe',
                          'Contrôler le nombre d’aligneurs et les étiquettes',
                          'Scanner le QR code en en-tête pour la traçabilité du lot',
                          'Reporter toute anomalie avant emballage',
                        ].map((item) => (
                          <li key={item} className="flex items-start break-inside-avoid">
                            <span className="text-primary mr-2">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                <div
                  ref={labelsRef}
                  className="mt-6 labels-print-root ipr-sheet-shell print:mt-0 print:shadow-none print:border-0 overflow-hidden"
                >
                  <ProductionSheetLabels
                    form={form}
                    caseNumber={patientData?.caseNumber}
                    patientName={patientData?.patientName}
                    packagingDate={form?.packaging_date || batch?.packaging_date}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 8mm;
          }

          html, body {
            background: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          body * {
            visibility: hidden;
          }

          body.print-sheet-only .production-print-root,
          body.print-sheet-only .production-print-root * {
            visibility: visible;
          }

          body.print-labels-only .labels-print-root,
          body.print-labels-only .labels-print-root * {
            visibility: visible;
          }

          body.print-sheet-only .production-print-root,
          body.print-labels-only .labels-print-root {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }

          .ipr-pdf-hide,
          .print\\:hidden {
            display: none !important;
          }

          button {
            display: none !important;
          }

          .production-sheet-page-1 {
            box-shadow: none !important;
            border: none !important;
            border-radius: 0 !important;
            max-height: 277mm;
            overflow: hidden;
          }

          .production-labels-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 4mm;
          }

          .production-label-card {
            border: 1px solid #cbd5e1;
            border-radius: 3mm;
            padding: 2.5mm;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            break-inside: avoid;
            page-break-inside: avoid;
            min-height: 28mm;
          }

          .production-sheet-body textarea {
            height: 28px !important;
            min-height: 28px !important;
          }

          .ipr-sheet-shell {
            box-shadow: none !important;
            border-radius: 0 !important;
          }
        }

        .production-labels-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
          gap: 0.75rem;
        }

        .production-label-card {
          border: 1px solid #e2e8f0;
          border-radius: 0.5rem;
          padding: 0.6rem 0.5rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: #fff;
          min-height: 6.5rem;
        }

        .ipr-pdf-export .ipr-pdf-hide {
          display: none !important;
        }
      `}</style>
    </div>
  );
};

export default ProductionSheetPage;
